import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
} from '@mui/material';
import { useEffect, useState, type CSSProperties } from 'react';
import { Loader } from '../loader/loader';
import { formatContextValue } from '@/lib/formatters';
import {
  getContextDisplayLabel,
  isContextDisplaySectionBreak,
  isHiddenContextField,
} from '@cloudcost/shared-types/dispute-context-display';
import { Pill } from '@/components/ds';
import { T, statusTone } from '@/theme/tokens';

const kvLabelStyle: CSSProperties = {
  color: T.textFaint,
  fontWeight: 700,
  fontSize: '0.8125rem',
};
const kvValueStyle: CSSProperties = {
  color: T.text,
  fontWeight: 700,
  fontSize: '0.8125rem',
  textAlign: 'right',
};

export type DisputeDetailsResponse = {
  dispute: any;
  context: {
    initial: Record<string, any> | null;
    current: Record<string, any>;
    snapshots?: Array<{ captured_at: string; context: Record<string, any> }>;
  };
};

interface DisputeContextDetailsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  disputeId: number | null;
}

/**
 * Renders a key/value object as a list of rows. Handles numbers (format as currency when appropriate) and other values.
 */
function ContextKeyValueList({ data }: { data: Record<string, any> }) {
  const entries = Object.entries(data).filter(
    ([key]) => !isHiddenContextField(key),
  );
  if (entries.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 2 }}>
      {entries.map(([key, value]) =>
        isContextDisplaySectionBreak(key) ? (
          <Box key={key} sx={{ height: 12 }} aria-hidden />
        ) : (
          <Box
            key={key}
            display="flex"
            alignItems="center"
            flexDirection="row"
            gap={2}
            justifyContent="space-between">
            <span style={kvLabelStyle}>{getContextDisplayLabel(key)}</span>
            {/status/i.test(key) ? (
              <Pill tone={statusTone(String(value))}>{formatContextValue(value)}</Pill>
            ) : (
              <span style={kvValueStyle}>{formatContextValue(value)}</span>
            )}
          </Box>
        ),
      )}
    </Box>
  );
}

/**
 * Full context modal: shows full history of context updates (all snapshots).
 * Opened by "View full context" from the main dispute window.
 */
const DisputeContextDetailsModal = ({
  open,
  setOpen,
  disputeId,
}: DisputeContextDetailsModalProps) => {
  const [data, setData] = useState<DisputeDetailsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !disputeId) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetchWrapper
      .get(urls.DevOps.getDispute.replace(':id', String(disputeId)))
      .then((response: any) => {
        setData(response as DisputeDetailsResponse);
      })
      .catch((err) => {
        setError(err?.message ?? 'Failed to load dispute');
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, disputeId]);

  const handleClose = () => {
    setOpen(false);
    setData(null);
    setError(null);
  };

  const snapshots = data?.context?.snapshots ?? [];
  const current = data?.context?.current;
  const displayItems =
    snapshots.length > 0
      ? snapshots
      : current != null && typeof current === 'object' && Object.keys(current).length > 0
        ? [{ captured_at: null as string | null, context: current }]
        : [];

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {loading && <Loader style={{ maxHeight: 200, minHeight: 200 }} />}
      {!loading && (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography
              sx={{ fontWeight: 900, fontSize: '1.15rem', color: T.text, letterSpacing: '-.2px' }}>
              {data?.dispute?.subject ?? 'Dispute context'} — Full context
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{ overflowY: 'auto', maxHeight: '60vh' }}>
            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            {data && !error && (
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                {displayItems.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No context history yet.
                  </Typography>
                ) : (
                  displayItems.map((item, index) => (
                    <Box key={index}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 900,
                          color: T.textFaint,
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '.4px',
                          mb: 0.75,
                        }}>
                        {item.captured_at
                          ? new Date(item.captured_at).toLocaleString()
                          : 'Current'}
                      </Typography>
                      <ContextKeyValueList data={item.context ?? {}} />
                    </Box>
                  ))
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
            <Button
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: T.textFaint,
                borderRadius: `${T.radiusSm}px`,
              }}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  zIndex: 999999999,
  '& .MuiDialog-paper': {
    width: 500,
  },
}));

export default DisputeContextDetailsModal;
